import React, { useEffect, useState } from "react";
import { GET_SERVICE_ITEMS } from "../../../../utils/GraphQL/queries";
import "./createQuoteModal.css";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import { useQuery } from "@apollo/client";
import {
  priceStrFormatter,
  removePriceStrFormatter,
} from "../../../../utils/formatters/priceStrFormatter";

const clearedNewItem = {
  itemName: "",
  price: "0",
  quantity: "",
  discountApplicable: false,
  jobType: "",
  payBucket: null,
  customPayCents: null,
  requiresPhotos: null,
};

const NewQuoteItemBox = ({ discountPercent, addQuoteItem, handleSnack }) => {

  const { data: servicesData } = useQuery(GET_SERVICE_ITEMS);
  const [customPrice, setCustomPrice] = useState(false);
  const [totalDisplay, setTotalDisplay] = useState("0");
  const [newItem, setNewItem] = useState({ ...clearedNewItem });

  useEffect(() => {
    if (newItem?.itemName !== "") {
      totalHandler(
        newItem?.price,
        newItem?.quantity,
        newItem?.discountApplicable
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountPercent]);

  const totalHandler = (price, quantity, discount) => {
    const total = discount
      ? ((((100 - discountPercent) / 100) * price * quantity) / 100)
          ?.toFixed(2)
          .replace(".", "")
      : ((price * quantity) / 100)?.toFixed(2).replace(".", "");

    setTotalDisplay(total);
  };

  const handleNewItem = (event) => {
    const name = event.target.name;
    let value = event.target.value;

    if (name === "itemName") {
      setCustomPrice(false);
      const selected = servicesData?.getServicesItems?.filter(
        (item) => item?.itemName === value
      )[0];

      setNewItem((prev) => {
        const singlePrice = parseInt(selected?.price) || 0;
        const howMany = parseInt(prev?.quantity) || 1;

        totalHandler(
          singlePrice,
          howMany,
          selected?.discountApplicable || false
        );

        return {
          ...prev,
          itemName: selected?.itemName,
          price: singlePrice?.toString(),
          quantity: howMany?.toString(),
          discountApplicable: selected?.discountApplicable || false,
          jobType: selected?.jobType,
          // Hardware Change refactor: snapshot payBucket + requiresPhotos
          // from the catalog onto the line item. Without this, payCalc's
          // fallback routes HC items to the refinish bucket (since
          // 'Hardware Change' doesn't match the tip or maintenance regex)
          // and the refinisher gets paid at their refinish rate instead
          // of 100%.
          payBucket: selected?.payBucket || null,
          // 'custom' bucket amount snapshots the same way (2026-10-06): a
          // later catalog edit never re-prices this line's refinisher pay.
          customPayCents: selected?.customPayCents ?? null,
          requiresPhotos:
            typeof selected?.requiresPhotos === "boolean"
              ? selected.requiresPhotos
              : null,
        };
      });

      return;
    }

    if (name === "quantity") {
      // setCustomPrice(false);
      setNewItem((prev) => {
        const selected = servicesData?.getServicesItems?.filter(
          (item) => item?.itemName === prev?.itemName
        )[0];

        const singlePrice = customPrice
          ? parseInt(prev?.price)
          : parseInt(selected?.price) || 0;
        const howMany = parseInt(value) || 1;

        if (!customPrice) {
          totalHandler(
            singlePrice,
            howMany,
            selected?.discountApplicable || false
          );
        }

        if (customPrice) {
          totalHandler(singlePrice, 1, false);
        }

        return {
          ...prev,
          price: singlePrice?.toString(),
          quantity: howMany?.toString(),
        };
      });

      return;
    }

    if (name === "price") {
      setCustomPrice(true);
      setNewItem((prev) => {
        value = removePriceStrFormatter(value);

        totalHandler(parseInt(value), 1, false);

        return {
          ...prev,
          price: value,
        };
      });

      return;
    }

    if (name === "discountApplicable") {
      // setCustomPrice(false);
      setNewItem((prev) => {
        const selected = servicesData?.getServicesItems?.filter(
          (item) => item?.itemName === prev?.itemName
        )[0];
        const singlePrice = customPrice
          ? parseInt(prev?.price)
          : parseInt(selected?.price) || 0;
        const howMany = parseInt(prev?.quantity) || 1;

        if (!customPrice) {
          totalHandler(singlePrice, howMany, value || false);
        }

        if (customPrice) {
          totalHandler(singlePrice, 1, false);
        }

        return {
          ...prev,
          price: singlePrice?.toString(),
          discountApplicable: value,
        };
      });

      return;
    }
  };

  const resetPrice = () => {
    setCustomPrice(false);
    setNewItem((prev) => {
      const selected = servicesData?.getServicesItems?.filter(
        (item) => item?.itemName === prev?.itemName
      )[0];

      const singlePrice = parseInt(selected?.price) || 0;
      const howMany = parseInt(prev?.quantity) || 1;

      totalHandler(singlePrice, howMany, prev?.discountApplicable || false);

      return {
        ...prev,
        price: singlePrice?.toString(),
        quantity: howMany?.toString(),
      };
    });

    return;
  };

  return (
    <div className="cq-v2-new-item-box">
      <div className="cq-v2-new-item-row">
        {/*
          Both of these use the capped popup rather than a native <select>: the
          catalog grows with every service item added, and Qty is a fixed 30.
          A native list is OS chrome, so CSS cannot cap or clip it — at 31 rows
          it ran off the top of the browser window, well outside the modal.
        */}
        {/* Item Select */}
        <div className="cq-v2-select-group cq-v2-item-select-wide">
          <span className="cq-v2-field-label">Item</span>
          <BdrSelectPopup
            id="cq-new-item-name"
            className="bdr-input bdr-select-trigger"
            name="itemName"
            value={newItem?.itemName}
            onChange={handleNewItem}
            placeholder="-- Select Item --"
            options={
              servicesData?.getServicesItems?.length > 0
                ? [...servicesData.getServicesItems]
                    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
                    .map((item) => ({
                      value: item?.itemName,
                      label: item?.itemName,
                    }))
                : []
            }
          />
        </div>

        {/* Quantity Select */}
        <div className="cq-v2-select-group cq-v2-item-select-narrow">
          <span className="cq-v2-field-label">Qty</span>
          <BdrSelectPopup
            id="cq-new-item-qty"
            className="bdr-input bdr-select-trigger"
            name="quantity"
            value={newItem?.quantity}
            onChange={handleNewItem}
            placeholder="-"
            options={[...Array(30)].map((_, index) => {
              const num = (index + 1).toString();
              return { value: num, label: num };
            })}
          />
        </div>

        {/* Price Input */}
        <div className="cq-v2-select-group cq-v2-item-select-narrow">
          <span className="cq-v2-field-label">
            {customPrice ? "Custom $" : "Price"}
          </span>
          <input
            className="bdr-input"
            type="tel"
            inputMode="tel"
            name="price"
            value={priceStrFormatter(totalDisplay)}
            onChange={handleNewItem}
          />
        </div>

        {/* Apply Discount Select */}
        <div className="cq-v2-select-group cq-v2-item-select-narrow">
          <span className="cq-v2-field-label">Discount?</span>
          <BdrSelectPopup
            id="cq-new-item-discount"
            className="bdr-input bdr-select-trigger"
            name="discountApplicable"
            value={newItem?.discountApplicable ? "true" : "false"}
            onChange={(e) => {
              handleNewItem({
                target: {
                  name: "discountApplicable",
                  value: e.target.value === "true",
                },
              });
            }}
            options={[
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ]}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="cq-v2-new-item-actions">
        {customPrice && (
          <BdrButton size="sm" onClick={resetPrice}>
            Reset Price
          </BdrButton>
        )}
        {/* solid: the host modal is variant="panel" (§6b) */}
        <BdrButton
          size="sm"
          variant="confirm-solid"
          onClick={() => {
            if (newItem?.itemName !== "") {
              addQuoteItem(newItem);
              setNewItem({ ...clearedNewItem });
              setCustomPrice(false);
              setTotalDisplay("0");
              handleSnack("Item Added", 2000);
              return;
            }

            handleSnack("Please select an item", 2500);
            return;
          }}
        >
          + Add Item
        </BdrButton>
      </div>
    </div>
  );
};

export default NewQuoteItemBox;
